<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Link } from '@inertiajs/vue3'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const sliderNavRef = ref(null)
const sliderStartAnimation = ref(null)

const props = defineProps({
    products: {
        type: Array,
        required: true,
    },
});

const currentIndex = ref(0);
const isAutoplay = ref(false);
const indicatorWidth = ref('0%'); // Reactive variable to control indicator fill
let autoplayInterval = null;

// Function to go to a specific slide and stop autoplay
const goToSlide = (index) => {
    currentIndex.value = index;
    stopAutoplay();
};

// Computed style for carousel translation
const carouselStyle = computed(() => {
    return { transform: `translateX(-${currentIndex.value * 100}%)`, transition: 'transform 0.5s ease-in-out' };
});

// Start autoplay and reset indicator width
const startAutoplay = () => {
    isAutoplay.value = true;
    indicatorWidth.value = '0%';

    setTimeout(() => {
        indicatorWidth.value = '100%';
        autoplayInterval = setInterval(() => {
            nextSlide();
        }, 7500);
    }, 10);
};

// Stop autoplay and clear interval
const stopAutoplay = () => {
    isAutoplay.value = false;
    clearInterval(autoplayInterval);
    indicatorWidth.value = '0%';
    console.log`Autoplay stopped ${isAutoplay.value} ${autoplayInterval}`;
};

// Move to the next slide and reset indicator width if autoplay is on
const nextSlide = () => {
    if (currentIndex.value < props.products.length - 1) {
        currentIndex.value++;
    } else {
        stopAutoplay();
    }
};

// Trigger animations on mount
onMounted(() => {
    // Slide-in animation for the navigation
    gsap.fromTo(sliderNavRef.value, { y: '-60vh' }, { y: '0vh', duration: 2, ease: 'power1.in', scrollTrigger: { trigger: sliderStartAnimation.value, start: 'top 100%', end: 'top 15%', scrub: true, invalidateOnRefresh: true, }});

    // Start autoplay when slider enters the viewport
    ScrollTrigger.create({
        trigger: sliderStartAnimation.value,
        start: 'top 100%',
        onEnter: () => startAutoplay(),
    });
});

// Clear interval on unmount
onBeforeUnmount(() => {
    stopAutoplay();
});
</script>

<template>
    <div ref="sliderStartAnimation" class="relative w-full mx-auto">
        <div class="flex w-full" :style="carouselStyle">
            <div v-for="(product, index) in products" :key="product.id" class="flex-shrink-0 px-4 w-full flex justify-center items-center transition-opacity duration-500 ease-in-out" :class="{ 'cursor-pointer': currentIndex !== index }" @click="goToSlide(index)">
                <div :class="`aspect-video ${product.color} rounded-2xl w-full h-auto flex flex-row overflow-hidden`">
                    <div class="flex flex-col max-w-md m-8 p-8" v-motion :initial="{ x: 100, opacity: 0 }" :visible="{ x: 0, opacity: 1, transition: { delay: 150, type: 'spring', stiffness: '50', damping: '12.5', duration: 1500 } }">
                        <hgroup>
                            <h2 class="text-4xl font-medium">{{ product.name }}</h2>
                            <p class="text-xl font-medium mt-4">{{ product.description }}</p>
                        </hgroup>
                        <div class="mt-12 text-center flex items-start">
                            <!-- href word product name maar die heeft soms spaties en hoofdletters dus dat even afvangen :D -->
                            <Link href="/project-detail" class="inline-flex justify-center items-center px-8 py-3 text-gray-900 bg-white hover:bg-white/85 transition-all duration-500 rounded-full font-medium text-xl tracking-wide cursor-pointer">
                                Bekijk dit project
                            </Link>
                        </div>
                    </div>
                    <div class="py-12 pr-12 h-full w-full flex justify-end">
                        <div :class="`mockup-browser bg-${[product.projectColor]}-200/65 border-${product.projectColor}-400/65 border w-full`">
                            <a :href="product.href" target="_blank" class="mockup-browser-toolbar">
                                <div class="input select-none text-sm pt-1">{{ product.href.replace('https://www.', '') }}</div>
                            </a>
                            <div :class="`border-${product.projectColor}-400/65 h-full bg-sjht flex border-t w-full relative`">
                                <!-- hier komt een blok per project met een logo en item van het project -->
                                <div v-html="product.sliderMockup"></div>
                              
                                <!-- <div class='absolute left-0 bottom-0'>
                                    <img :src="'/img/projects/sjht/sjht_r_big_hero_4.webp'" class='h-full w-full object-contain' />
                                </div>

                                <div class='h-14 mt-10 ml-10'>
                                    <img :src="'/img/projects/sjht/sjht-logo.svg'" class='h-full w-auto' />
                                </div>

                                <div class='relative flex flex-col items-end justify-center pb-12 md:pb-10 lg:pb-0 gap-12 ml-auto mr-14 w-1/2'>
                                    <h1 class='font-ROC-Grotesk max-w-[18rem] md:max-w-xl break-words text-shadow-lg md:text-shadow'>Reparaties zo voor elkaar.</h1>
                                </div> -->

                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div ref="sliderNavRef" class="flex justify-center space-x-2 mt-12">
            <button class="bg-gray-200 rounded-full h-12 w-12" @click="isAutoplay ? stopAutoplay() : startAutoplay()">
                <font-awesome-icon :icon="['fas', isAutoplay && currentIndex < props.products.length - 1 ? 'pause' : 'play']" class="text-gray-900" />
            </button>
            <div class="bg-gray-200 rounded-full h-12 w-auto items-center px-3.5 flex flex-row gap-2 relative">
                <div v-for="(product, index) in products" :key="product.id" :class="['h-2 bg-gray-600 rounded-full cursor-pointer transition-all duration-1000 ease-in-out relative overflow-hidden', index === currentIndex ? 'w-12' : 'w-2']" @click="goToSlide(index)">
                    <div :class="['absolute left-0 h-full', isAutoplay ? 'bg-gray-900 transition-all duration-[7500ms] ease-linear' : 'bg-gray-900']" :style="{ width: index === currentIndex ? indicatorWidth : '0%' }"></div>
                </div>
            </div>
        </div>
    </div>
</template>
